/**
 * WEB-3447 — дверь ленивого куска рамки экрана эталона Slimkit
 * (`quizChromeKit.ts`). Решение владельца 16.10.2026 по WEB-3128: «ленивые
 * куски» вместо подъёма потолка веса первого экрана.
 *
 * КТО ЖДЁТ. Страница квиза (`PublicQuiz`, `usePlayerKitDoor`) — вместе со
 * стиль-паком, до первого кадра: пак воронки включает рамку
 * (`packNeedsQuizChromeKit`) — кусок заказывается, и экран не рисуется, пока
 * его нет. Визард (`useQuizChromeKit`) берёт набор ОДИН РАЗ при монтировании:
 * кусок на руках — настоящий, нет — запасной `NO_QUIZ_CHROME_KIT` (правила
 * хуков: набор не меняется посреди жизни визарда). Кабинет кладёт кусок
 * статически (`main.tsx`), тестовая обвязка — синхронным добытчиком.
 *
 * ЗАПАСНОЙ НАБОР — то, что настоящий отдаёт паку без рамки: шапка не
 * прячется (кроме полей экрана «скрыть шапку» и «скрыть лого» — они работают
 * у любого пака и есть и здесь), «вперёд» нет, переход по кнопкам сразу и
 * синхронно, прибитой кнопки и шапки нет, вид кнопки «Далее» — из темы.
 * Равенство судит `quizChromeKitDoor.test.tsx` по всем пакам каталога и по
 * каждому признаку из `packNeedsQuizChromeKit`.
 *
 * ГРАНИЦА. Признак перечисляет поля пака, которые включают рамку. Новое поле
 * рамки, не внесённое сюда, у пака без прочих признаков молча не сработает у
 * покупателя (в кабинете и тестах набор настоящий) — тест равенства ловит его,
 * только если такой пак есть в каталоге. Кусок отказал (выкат унёс файл после
 * одной перезагрузки) — воронка рисуется запасным набором, без рамки.
 */
import {
  createElement,
  Fragment,
  useState,
  type ComponentProps,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { playerKitDoor } from '@/player/playerKitDoor';
import type { StylePack } from '@/themes/stylePack.types';
import { getStylePackById } from '@/themes/stylePacks';
import { normalizeAdvanceDelayMs } from './autoAdvanceDelay';
import { resolveQuizCta } from './quizCta';
import type { QuizChromeExtras } from './useQuizChromeExtras';

type FullKit = (typeof import('./quizChromeKit'))['QUIZ_CHROME_KIT'];

/** Форма набора: у настоящего и у запасного одна. */
export interface QuizChromeKit {
  useQuizChromeExtras: FullKit['useQuizChromeExtras'];
  QuizSectionHeader: ComponentType<
    ComponentProps<FullKit['QuizSectionHeader']>
  >;
  ForwardChevronButton: ComponentType<
    ComponentProps<FullKit['ForwardChevronButton']>
  >;
  sectionProgressForVisitor: (
    input: Parameters<FullKit['sectionProgressForVisitor']>[0],
  ) => ReturnType<FullKit['sectionProgressForVisitor']> | null;
  ChevronIcon: ComponentType<ComponentProps<FullKit['ChevronIcon']>>;
  CHEVRON_BOX_STYLE: CSSProperties;
  chevronSlotStyle: FullKit['chevronSlotStyle'];
  CtaDock: ComponentType<ComponentProps<FullKit['CtaDock']>>;
  ctaPackButtonStyle: FullKit['ctaPackButtonStyle'];
  DOCKED_WRAP_STYLE: CSSProperties;
  QuizChromeFrame: ComponentType<ComponentProps<FullKit['QuizChromeFrame']>>;
  DesktopLogo: ComponentType<ComponentProps<FullKit['DesktopLogo']>>;
  QuizCloseButton: ComponentType<ComponentProps<FullKit['QuizCloseButton']>>;
  ArrowBackIcon: ComponentType<ComponentProps<FullKit['ArrowBackIcon']>>;
  CIRCLE_SOLID_STYLE: CSSProperties;
  QuizSectionStepsProgress: ComponentType<
    ComponentProps<FullKit['QuizSectionStepsProgress']>
  >;
}

/**
 * Пак включает рамку Slimkit — воронке нужен кусок. Каждое условие — то
 * место, где настоящий набор ведёт себя иначе, чем запасной.
 */
export function packNeedsQuizChromeKit(pack: StylePack | undefined): boolean {
  if (!pack) return false;
  const { chrome, cta, states } = pack;
  return (
    // шапка: узлы по разделам, шевроны, лого слева, прибитая и вне экрана
    chrome.progress === 'section-nodes' ||
    chrome.backButton === 'chevron' ||
    chrome.forwardButton === 'chevron' ||
    chrome.logo === 'desktop-left' ||
    chrome.persistent === true ||
    !!chrome.sticky ||
    chrome.hideOnKeyboard === true ||
    // нижняя кнопка: прибита к низу, свой вид из пака
    cta.placement === 'fixed-bottom' ||
    cta.typography !== undefined ||
    cta.shadow !== undefined ||
    cta.maxWidthPx !== undefined ||
    cta.disabledStyle === 'muted-brand-dark' ||
    // переход: пауза по кнопкам, прокрутка в начало
    normalizeAdvanceDelayMs(states.advanceDelayMs) > 0 ||
    states.resetScrollOnEnter === true
  );
}

const runNow = (action: () => boolean | void): boolean => action() !== false;

const nothing = (): null => null;

const passThrough = ({ children }: { children?: ReactNode }) =>
  createElement(Fragment, null, children);

function useNoQuizChromeExtras({
  stylePackId,
  screen,
  quizCta,
}: Parameters<FullKit['useQuizChromeExtras']>[0]): QuizChromeExtras {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  return {
    pack,
    visibility: {
      hideChrome: screen?.hideChrome === true,
      hideLogo: screen?.hideLogo === true,
    },
    forwardEnabled: false,
    forwardVisible: false,
    runNav: runNow,
    dock: null,
    canScroll: false,
    persistent: false,
    // Положение и вид выключенной главной кнопки — та же дверь, что у
    // настоящего набора; прибитой панели без куска нет (quizCtaNeedsChromeKit).
    cta: resolveQuizCta(pack?.cta, quizCta, screen?.ctaPlacement),
  };
}

/** Запасной набор: воронке рамка не нужна (или её кусок отказал). */
export const NO_QUIZ_CHROME_KIT: QuizChromeKit = {
  useQuizChromeExtras: useNoQuizChromeExtras,
  QuizSectionHeader: nothing,
  ForwardChevronButton: nothing,
  sectionProgressForVisitor: nothing,
  ChevronIcon: nothing,
  CHEVRON_BOX_STYLE: {},
  chevronSlotStyle: () => ({}),
  CtaDock: passThrough,
  ctaPackButtonStyle: () => ({}),
  DOCKED_WRAP_STYLE: {},
  QuizChromeFrame: passThrough,
  DesktopLogo: nothing,
  QuizCloseButton: nothing,
  ArrowBackIcon: nothing,
  CIRCLE_SOLID_STYLE: {},
  QuizSectionStepsProgress: nothing,
};

/*
 * WEB-3447, заход 3: свой файл и своя дверь у куска сняты — рамка едет в
 * едином куске плеера (`player/playerKit.ts`, дверь `playerKitDoor`); ждёт
 * страница одной дверью (`player/usePlayerKitDoor.ts`).
 */

/** Набор визарда — один на всю жизнь визарда (см. шапку файла). */
export function useQuizChromeKit(): QuizChromeKit {
  const [kit] = useState<QuizChromeKit>(
    () => playerKitDoor.get()?.chrome ?? NO_QUIZ_CHROME_KIT,
  );
  return kit;
}
